/**
 * Background sync coordinator — the single owner of "re-fetch everything now".
 *
 * The bug this replaces: five feature sagas each independently took
 * `ws/reconnected` and immediately fired a request through the shared
 * axios instance. Nothing owned that fan-out, so nothing could tell it
 * was a bad moment to run. When the tab had been frozen or the host had
 * slept, the WebSocket eventually re-dialled, all five requests went out
 * over sockets that no longer existed, and all five aborted together
 * (`ECONNABORTED`, `reached_server: false`, `online: true`) — at the 10s
 * timeout if the tab was merely throttled, up to half an hour later if it
 * was frozen and the timeout callback couldn't be dispatched until thaw.
 * With no retry anywhere, that cycle's data was simply lost until the
 * next reconnect. QPL showed ~20 such cycles a day.
 *
 * The coordinator makes a cycle an explicit, observable thing:
 *
 *   trigger  →  wait until visible  →  probe reachability  →  sync/refresh
 *
 * and drops the cycle only after a bounded probe loop has actually failed
 * — never silently. `takeLatest` collapses overlapping triggers (a resume
 * is usually followed by a refocus and then a WS reconnect within
 * seconds) into one cycle, which is also what keeps the five requests
 * from being issued twice over.
 */

import { SagaIterator } from 'redux-saga';
import { call, cancelled, delay, put, take, takeLatest } from 'redux-saga/effects';
import { EventChannel } from 'redux-saga';
import { emitPoint, finalizeFlow, startFlow } from '@/features/qpl-client';
import { WS_RECONNECTED } from '../ws/root-saga';
import { TAB_REFOCUSED } from '../tab-focus/saga';
import { abortInFlightRequests } from '../api/client';
import { fullJitterDelayMs } from '../api/retry';
import { APP_RESUMED, SyncReason, syncRefresh } from './actions';
import { createVisibleChannel, isTabHidden } from './visibility';
import { probeReachable } from './connectivity';

/** Bounded probe loop — roughly half a minute of trying before we give up. */
export const PROBE_MAX_ATTEMPTS = 5;
export const PROBE_BACKOFF_BASE_MS = 1000;
export const PROBE_BACKOFF_MAX_MS = 15000;

const REASON_BY_TRIGGER: Record<string, SyncReason> = {
  [WS_RECONNECTED]: 'ws_reconnected',
  [APP_RESUMED]: 'app_resumed',
  [TAB_REFOCUSED]: 'tab_refocused',
};

export const SYNC_TRIGGERS = [WS_RECONNECTED, APP_RESUMED, TAB_REFOCUSED];

/** Blocks until the document is visible. Returns immediately if it is. */
export function* waitUntilVisible(): SagaIterator {
  const channel = (yield call(createVisibleChannel)) as EventChannel<true>;
  try {
    yield take(channel);
  } finally {
    channel.close();
  }
}

/**
 * Probe until the server answers or the attempts run out. Backs off with
 * full jitter so a household of two tabs waking from the same suspension
 * don't probe in lockstep.
 */
export function* waitForReachable(flowId: string): SagaIterator {
  for (let attempt = 1; attempt <= PROBE_MAX_ATTEMPTS; attempt += 1) {
    const reachable = (yield call(probeReachable)) as boolean;
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    if (reachable) {
      emitPoint(flowId, 'probe_ok', { attempt, online });
      return true;
    }
    emitPoint(flowId, 'probe_failed', { attempt, online });
    if (attempt < PROBE_MAX_ATTEMPTS) {
      yield delay(
        fullJitterDelayMs(attempt, Math.random, PROBE_BACKOFF_BASE_MS, PROBE_BACKOFF_MAX_MS),
      );
    }
  }
  return false;
}

export function* runSyncCycle(action: { type: string; payload?: { gapMs?: number } }): SagaIterator {
  const reason = REASON_BY_TRIGGER[action.type] || 'ws_reconnected';
  const handle = startFlow('sync.cycle', { reason });
  try {
    // A resume means every request still outstanding is stranded on a
    // socket that died during the suspension. Abort them so they fail
    // now instead of when the browser eventually notices — the cycle
    // below re-fetches the same data over fresh connections anyway.
    if (action.type === APP_RESUMED) {
      const aborted = (yield call(abortInFlightRequests)) as number;
      emitPoint(handle.id, 'stranded_requests_aborted', {
        count: aborted,
        gap_ms: action.payload?.gapMs ?? 0,
      });
    }

    if (yield call(isTabHidden)) {
      emitPoint(handle.id, 'cycle_deferred_hidden', {});
      yield call(waitUntilVisible);
      emitPoint(handle.id, 'cycle_resumed_visible', {});
    }

    const reachable = (yield call(waitForReachable, handle.id)) as boolean;
    if (!reachable) {
      // Explicitly a dropped cycle — but a recorded one. The next
      // trigger (the WS reconnect loop keeps retrying) starts a fresh
      // cycle; this flow is what makes the gap visible in the dashboard
      // instead of a silent hole in the data.
      emitPoint(handle.id, 'cycle_abandoned_unreachable', {
        attempts: PROBE_MAX_ATTEMPTS,
      });
      finalizeFlow(handle.id, 'FAILED');
      return;
    }

    yield put(syncRefresh(reason));
    emitPoint(handle.id, 'cycle_dispatched', {});
    finalizeFlow(handle.id, 'SUCCESS');
  } finally {
    // takeLatest cancels this cycle when a newer trigger arrives; that's
    // the intended de-duplication, not a failure.
    if (yield cancelled()) {
      finalizeFlow(handle.id, 'CANCELED', 'superseded');
    }
  }
}

export function* syncCoordinatorSaga(): SagaIterator {
  yield takeLatest(SYNC_TRIGGERS, runSyncCycle);
}
